import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { isEmail, Match, Show, Switch } from '@side/pantry';
import { useController, useFieldArray, useWatch } from 'react-hook-form';
import { TrackOfferEvent } from 'src/modules/analytics/useTrackAnalytics/domains/offers/events';
import { useTrackOfferAnalytics } from 'src/modules/analytics/useTrackAnalytics/domains/offers/useTrackOfferAnalytics';
import { FormTextField } from '@/components/Inputs/FormTextField';
import {
  BUYER_TYPES,
  ENTITY_OPTIONS,
  MAX_CLIENT_GROUP_CLIENTS,
  PROBATE_OPTIONS,
  TRUST_OPTIONS_NO_SUCCESSOR,
} from '@/constants';
import type { CreateOfferFormData } from '../../types';
import { NewBuyerFormGroup } from './NewBuyerFormGroup';

const ClientTitleRadioGroup = ({
  name,
  textFieldName,
  options,
}: {
  name: string;
  textFieldName: string;
  options: { label: string; value: string }[];
}) => {
  const { field } = useController({ name });

  return (
    <Grid size={12}>
      <FormControl>
        <FormLabel
          sx={{ typography: 'subtitle2', color: 'text.primary', mb: 0.5 }}
        >
          Client Title
        </FormLabel>
        <RadioGroup {...field} row>
          {options.map((option) => (
            <FormControlLabel
              key={`${name}-${option.label}`}
              value={option.value}
              control={<Radio name={option.value} />}
              label={option.label}
            />
          ))}
        </RadioGroup>
      </FormControl>
      <Show when={field.value === 'other'}>
        <FormTextField
          name={textFieldName}
          required
          placeholder="Please specify"
          fixedHeight
        />
      </Show>
    </Grid>
  );
};

const SuccessorCheckbox = ({ name }: { name: string }) => {
  const { field } = useController({ name });

  return (
    <Grid
      size={{ xs: 12, lg: 6, xl: 3 }}
      sx={{ alignContent: 'center', justifyContent: 'center' }}
    >
      <FormControlLabel
        label="Is Successor"
        {...field}
        control={
          <Checkbox
            checked={!!field.value}
            onChange={(event) => field.onChange(event.target.checked)}
          />
        }
      />
    </Grid>
  );
};

interface BuyerFormContentProps {
  index: number;
}

const BuyerFormContent = ({ index }: BuyerFormContentProps) => {
  const entityType = useWatch<
    CreateOfferFormData,
    `newBuyerClients.${number}.entityType`
  >({
    name: `newBuyerClients.${index}.entityType`,
  });
  const trackOfferEvent = useTrackOfferAnalytics();

  return (
    <Grid container columnSpacing={2} rowSpacing={2}>
      <Grid size={12}>
        <FormTextField
          name={`newBuyerClients.${index}.entityType`}
          select
          label="Ownership"
          required
          defaultValue={undefined}
          fixedHeight
          slotProps={{
            select: {
              displayEmpty: true,
              renderValue: (value: unknown) =>
                value ? (
                  BUYER_TYPES.find((option) => option.value === value)?.label
                ) : (
                  <Typography component="span" sx={{ color: 'text.disabled' }}>
                    Select
                  </Typography>
                ),
            },
          }}
          onChange={(e) => {
            trackOfferEvent(TrackOfferEvent.SelectBuyerType, {
              value: e.target.value,
              buyerOrder: index + 1,
            });
          }}
        >
          {BUYER_TYPES.map(({ value, label }) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </FormTextField>
      </Grid>

      <Grid size={{ xs: 6, md: 3 }}>
        <FormTextField
          name={`newBuyerClients.${index}.firstName`}
          label="First Name"
          required
          fixedHeight
        />
      </Grid>
      <Grid size={{ xs: 6, md: 3 }}>
        <FormTextField
          name={`newBuyerClients.${index}.lastName`}
          label="Last Name"
          required
          fixedHeight
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <FormTextField
          name={`newBuyerClients.${index}.email`}
          label="Email"
          required
          fixedHeight
          rules={{
            validate: (value: string) => {
              return !value || isEmail(value) || 'Invalid email';
            },
          }}
        />
      </Grid>
      <Switch>
        <Match when={entityType === 'trust'}>
          <Grid size={{ xs: 12, lg: 6, xl: 9 }}>
            <FormTextField
              name={`newBuyerClients.${index}.trust.trustName`}
              label="Trust Name"
              required
              placeholder="the John Smith Family Trust, dated 19XX"
              fixedHeight
            />
          </Grid>
          <SuccessorCheckbox name={`newBuyerClients.${index}.isSuccessor`} />
          <ClientTitleRadioGroup
            name={`newBuyerClients.${index}.trust.title`}
            textFieldName={`newBuyerClients.${index}.trust.titleOther`}
            options={TRUST_OPTIONS_NO_SUCCESSOR}
          />
        </Match>
        <Match when={entityType === 'entity'}>
          <Grid size={12}>
            <FormTextField
              name={`newBuyerClients.${index}.entity.entityName`}
              label="Entity Name"
              required
              placeholder="Fox Holdings, Inc"
              fixedHeight
            />
            <ClientTitleRadioGroup
              name={`newBuyerClients.${index}.entity.title`}
              textFieldName={`newBuyerClients.${index}.entity.titleOther`}
              options={ENTITY_OPTIONS}
            />
          </Grid>
        </Match>
        <Match when={entityType === 'probate'}>
          <Grid size={12}>
            <FormTextField
              name={`newBuyerClients.${index}.probate.estateName`}
              label="Estate Name"
              required
              placeholder="Estate of John Smith"
              fixedHeight
            />
            <ClientTitleRadioGroup
              name={`newBuyerClients.${index}.probate.title`}
              textFieldName={`newBuyerClients.${index}.probate.titleOther`}
              options={PROBATE_OPTIONS}
            />
          </Grid>
        </Match>
        <Match when={entityType === 'powerOfAttorney'}>
          <Grid size={12}>
            <Typography
              variant="subtitle2"
              sx={{ color: 'text.secondary', pb: 1 }}
            >
              Authorized Signer / Attorney in Fact:
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormTextField
              name={`newBuyerClients.${index}.powerOfAttorney.firstName`}
              label="First Name"
              required
              fixedHeight
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormTextField
              name={`newBuyerClients.${index}.powerOfAttorney.lastName`}
              label="Last Name"
              required
              fixedHeight
            />
          </Grid>
          <Grid size={12}>
            <FormTextField
              name={`newBuyerClients.${index}.powerOfAttorney.email`}
              label="Email"
              required
              fixedHeight
            />
          </Grid>
        </Match>
      </Switch>
    </Grid>
  );
};

export const BuyerFormsGroup = ({ sx }: { sx?: SxProps<Theme> }) => {
  const {
    append,
    remove,
    fields: buyers,
  } = useFieldArray({
    name: 'newBuyerClients',
    rules: { minLength: 1, maxLength: MAX_CLIENT_GROUP_CLIENTS },
  });
  const trackOfferEvent = useTrackOfferAnalytics();

  const handleAddBuyer = () => {
    trackOfferEvent(TrackOfferEvent.ClickAddBuyer, {
      buyerOrder: buyers.length + 1,
    });
    append({
      firstName: undefined,
      lastName: undefined,
      email: undefined,
      entityType: '',
    });
  };

  const handleRemoveBuyer = (index: number) => {
    if (buyers.length > 1) {
      trackOfferEvent(TrackOfferEvent.ClickRemoveBuyer, {
        buyerOrder: index + 1,
      });
      remove(index);
    }
  };

  const items = buyers.map((buyer, index) => ({
    id: buyer.id,
    label: buyers.length > 1 ? `Buyer ${index + 1}` : undefined,
    content: <BuyerFormContent index={index} />,
    onRemove: buyers.length > 1 ? () => handleRemoveBuyer(index) : undefined,
  }));

  return (
    <Box sx={sx}>
      <NewBuyerFormGroup
        items={items}
        maxItems={MAX_CLIENT_GROUP_CLIENTS}
        addItemLabel="Add Buyer"
        onAddItem={handleAddBuyer}
        itemSpacing={1.5}
      />
    </Box>
  );
};
